import React from "react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  const effectiveDate = "November 12, 2025";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-4xl px-6 py-16">
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
          >
            ← Back to DoodleMagic
          </Link>
        </div>
        <header className="space-y-4">
          <span className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Terms of Service
          </span>
          <h1 className="font-heading text-4xl font-bold">
            The agreement for using DoodleMagic
          </h1>
          <p className="text-base text-muted-foreground">
            Effective date: {effectiveDate}
          </p>
        </header>

        <section className="mt-10 space-y-6 text-base leading-relaxed text-muted-foreground">
          <p>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and
            use of DoodleMagic provided by The LFG Company (&ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By using the Service, you
            agree to these Terms. If you do not agree, please do not use the
            Service.
          </p>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              1. Eligibility &amp; accounts
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                You must have legal capacity to form a binding contract, or use
                the Service with parent or guardian consent if you are under 18.
              </li>
              <li>
                You are responsible for keeping your credentials secure and for
                activity that occurs under your account.
              </li>
              <li>
                If you suspect unauthorized use, notify us via the{" "}
                <Link to="/support" className="text-primary underline">
                  Support Page
                </Link>
                .
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              2. User content &amp; conduct
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                You retain ownership of drawings and other content you upload
                (&ldquo;User Content&rdquo;).
              </li>
              <li>
                You grant us a non-exclusive, worldwide, royalty-free license to
                use, host, modify, and create derivative works solely to operate
                and improve the Service.
              </li>
              <li>
                You confirm you have all rights needed to upload the drawings,
                including consent from the child&rsquo;s guardian if required.
              </li>
              <li>
                You will not upload content that is unlawful, abusive,
                defamatory, obscene, or infringes the rights of others.
              </li>
              <li>
                We may remove content or suspend accounts at our discretion when
                we believe these Terms have been violated.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              3. Generated output
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Generated videos and other outputs are provided &ldquo;as
                is.&rdquo; Review them before sharing.
              </li>
              <li>
                We do not guarantee accuracy, suitability, or exclusivity for
                any generated output. AI results may resemble content generated
                for other users.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              4. Payment terms
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                The current model is pay-as-you-go, with charges processed by
                Stripe. Future subscription plans may include additional terms.
              </li>
              <li>
                Fees are stated in USD unless otherwise noted and are
                non-refundable except as required by law.
              </li>
              <li>
                You authorize us and our payment provider to charge the payment
                method you supply.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              5. Third-party services
            </h2>
            <p>
              DoodleMagic relies on third-party providers such as Stripe,
              Supabase, Google Cloud Platform, and Google Gemini/Veo3. Your use
              may be subject to their terms and policies, and we are not
              responsible for their actions.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              6. Intellectual property
            </h2>
            <p>
              All Service components (software, design, trademarks) are owned by
              The LFG Company or our licensors. Except for your User Content, no
              rights are transferred unless expressly stated. You may not copy,
              modify, reverse engineer, distribute, or create derivative works
              from the Service except where permitted by law.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              7. Feedback
            </h2>
            <p>
              If you share feedback or suggestions, you grant us permission to
              use it without restriction or compensation.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              8. Termination
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>You may stop using the Service and request account deletion at any time.</li>
              <li>
                We may suspend or terminate access for any reason, including
                violations of these Terms.
              </li>
              <li>
                Upon termination, your license to use the Service ends, but
                sections that should survive (such as intellectual property,
                indemnification, and disclaimers) remain in effect.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              9. Disclaimer of warranties
            </h2>
            <p>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as
              available.&rdquo; We disclaim all warranties, express or implied,
              including merchantability, fitness for a particular purpose, and
              non-infringement. We do not guarantee the Service will be
              uninterrupted, error-free, or secure.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              10. Limitation of liability
            </h2>
            <p>
              To the fullest extent permitted by law, The LFG Company will not
              be liable for indirect, incidental, special, consequential, or
              punitive damages, or for loss of profits or data, arising from
              your use of the Service. Our total liability for all claims will
              not exceed the greater of $100 or the amounts you paid in the 12
              months preceding the claim.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              11. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold The LFG Company harmless from any
              claims, damages, liabilities, costs, or expenses (including
              reasonable attorneys&rsquo; fees) arising out of your use of the
              Service, your User Content, or your violation of these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              12. Governing law &amp; dispute resolution
            </h2>
            <p>
              These Terms are governed by the laws of the State of California,
              excluding its conflict-of-law rules. Any disputes will be resolved
              in the state or federal courts located in San Francisco County,
              California, unless otherwise required by law. You consent to the
              jurisdiction of those courts.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              13. Changes to these Terms
            </h2>
            <p>
              We may update these Terms from time to time. When material changes
              occur, we will update the effective date and provide notice
              through the Service. Continued use after changes means you accept
              the updated Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              14. Contact
            </h2>
            <p>
              For questions, support, or legal notices regarding these Terms,
              visit our{" "}
              <Link to="/support" className="text-primary underline">
                Support Page
              </Link>
              .
            </p>
          </section>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;

